import { isEditableElementFocused } from '@src/services/shortcuts/keyboard';

import type SaveStoryAndVideoService from '@src/services/SaveStoryAndVideoService';
import type { ScopeRegistration } from '@src/services/shortcuts/types';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';

interface GlobalShortcutDeps {
  videoCreator: IVideoCreatorStore;
  saveService: SaveStoryAndVideoService;
}

export function createGlobalShortcutScope({
  videoCreator,
  saveService,
}: GlobalShortcutDeps): ScopeRegistration {
  const togglePlayback = () => {
    if (videoCreator.isPlaying) {
      videoCreator.renderer?.pause();
    } else {
      videoCreator.refreshSourceAndPlay();
    }
  };

  const save = () => {
    void saveService.save(false);
  };

  const undo = () => {
    videoCreator.undo();
  };

  const redo = () => {
    videoCreator.redo();
  };

  return {
    scopeId: 'global',
    isActive: () => true,
    shortcuts: [
      {
        id: 'global.playToggle',
        chord: [{ code: 'Space' }, { code: 'KeyK' }],
        guard: () => !isEditableElementFocused(),
        run: togglePlayback,
      },
      {
        id: 'global.save',
        chord: [
          { code: 'KeyS', meta: true },
          { code: 'KeyS', ctrl: true },
        ],
        run: save,
      },
      // An editable field owns its own undo: with the pointer in the Creator
      // Studio title input, Cmd+Z ran the editor's undo and the preventDefault
      // that follows a handled shortcut cancelled the browser's text undo, so
      // backspaced characters never came back (PLAT-11503). The clip preview's
      // undo hotkeys already stand down the same way (UndoRedoButtons).
      {
        id: 'global.undo',
        chord: [
          { code: 'KeyZ', meta: true },
          { code: 'KeyZ', ctrl: true },
        ],
        guard: () => !isEditableElementFocused(),
        run: undo,
      },
      {
        id: 'global.redo',
        chord: [
          { code: 'KeyY', meta: true },
          { code: 'KeyY', ctrl: true },
          { code: 'KeyZ', meta: true, shift: true },
          { code: 'KeyZ', ctrl: true, shift: true },
        ],
        guard: () => !isEditableElementFocused(),
        run: redo,
      },
    ],
  };
}
